import { useTranslation } from 'react-i18next'
import { Wallet, TrendingUp, TrendingDown, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Separator } from '@/components/shadcn/separator'
import { Badge } from '@/components/shadcn/badge'
import { useGetApiWalletsWalletIdCompany } from '@/api/generated/company-management-user/company-management-user'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

export interface PointsBalance {
  currency: string
  totalCurrentBalance: number
  totalPendingBalance: number
  incomingCurrent: number
  outgoingCurrent: number
  incomingPending: number
  outgoingPending: number
}

// Fallback so the cards always render (as zeros) instead of crashing on missing data.
// A real load failure is surfaced by LoaderGeneral's error state, not by this default.
const EMPTY_BALANCE: PointsBalance = {
  currency: '',
  totalCurrentBalance: 0,
  totalPendingBalance: 0,
  incomingCurrent: 0,
  outgoingCurrent: 0,
  incomingPending: 0,
  outgoingPending: 0,
}

interface Props {
  /** Empty while the wallet id is still being resolved. */
  walletId: string
  /** Wallet list is still loading → only relevant while walletId is empty. */
  walletResolving?: boolean
  /** Wallet list failed to load → only relevant while walletId is empty. */
  walletResolveError?: boolean
}

function BalanceRow({ label, value, currency }: { label: string; value: number; currency: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums">
        {value.toLocaleString('de-DE')} {currency}
      </span>
    </div>
  )
}

export function WalletBalanceCards({ walletId, walletResolving, walletResolveError }: Props) {
  const { t } = useTranslation('wallet')

  const { data, isLoading, isError } = useGetApiWalletsWalletIdCompany(walletId, {
    query: { enabled: !!walletId },
  })
  // Always a valid object → the cards render even before/without data (zeros).
  const pb = (((data as any)?.data ?? data)?.pointsBalance as PointsBalance | null | undefined) ?? EMPTY_BALANCE

  // Without a walletId the balance query is disabled, so fall back to the wallet-list
  // resolution state — otherwise the cards would show zeros instead of loading/error.
  const showLoading = (!!walletId && isLoading) || (!walletId && !!walletResolving)
  const showError = isError || (!walletId && !!walletResolveError)

  return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader >
            <CardTitle className="flex items-center gap-2 text-base uppercase">
              <Wallet className="size-4 text-primary" />
              {t('balance.title')}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <LoaderGeneral isLoading={showLoading} isError={showError}>
              <div className="mb-1">
                <span className="text-4xl font-bold tabular-nums">
                  {pb.totalCurrentBalance.toLocaleString('de-DE')}
                </span>
                <span className="ml-2 text-muted-foreground text-lg">{pb.currency}</span>
              </div>
              {pb.totalPendingBalance > 0 && (
                <Badge variant="outline" className="text-xs mt-1">
                  <Clock className="size-3 mr-1" />
                  {pb.totalPendingBalance.toLocaleString('de-DE')} {pb.currency} {t('balance.pending')}
                </Badge>
              )}
            </LoaderGeneral>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm text-foreground font-semibold uppercase tracking-wider">
              <TrendingUp className="size-4 text-green-500" />
              {t('balance.sections.current')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LoaderGeneral isLoading={showLoading} isError={showError}>
              <BalanceRow label={t('balance.incoming')} value={pb.incomingCurrent} currency={pb.currency} />
              <Separator />
              <BalanceRow label={t('balance.outgoing')} value={pb.outgoingCurrent} currency={pb.currency} />
            </LoaderGeneral>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm text-foreground font-semibold uppercase tracking-wider">
              <TrendingDown className="size-4 text-red-500" />
              {t('balance.sections.pending')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LoaderGeneral isLoading={showLoading} isError={showError}>
              <BalanceRow label={t('balance.incoming')} value={pb.incomingPending} currency={pb.currency} />
              <Separator />
              <BalanceRow label={t('balance.outgoing')} value={pb.outgoingPending} currency={pb.currency} />
              <Separator />
              <BalanceRow label={t('balance.total')} value={pb.totalPendingBalance} currency={pb.currency} />
            </LoaderGeneral>
          </CardContent>
        </Card>
      </div>
  )
}
